import { useMsal } from '@azure/msal-react'
import { LineChart } from '@mantine/charts'
import {
  Alert,
  Anchor,
  Group,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchOrgMetrics } from '../../data/advertisement.service'
import { OrgMetricsDto } from '../../models/OrgMetricsDto'
import { useSessionInfo } from '../../contexts/SessionContext'
import { COLORS } from '../../utils/constants'

const StatCard = ({
  label,
  value,
  color,
  sub,
}: {
  label: string
  value: number
  color?: string
  sub?: string
}) => (
  <Paper withBorder p='md' radius='md'>
    <Text c='dimmed' size='sm' mb={4}>
      {label}
    </Text>
    <Title order={2} c={color}>
      {Math.round(value).toLocaleString()}
    </Title>
    {sub && (
      <Text size='xs' c='dimmed' mt={2}>
        {sub}
      </Text>
    )}
  </Paper>
)

type ChartView = 'impressions' | 'scans'

export default function MetricsPage() {
  const { instance } = useMsal()
  const { session } = useSessionInfo()
  const [data, setData] = useState<OrgMetricsDto | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [days, setDays] = useState('30')
  const [chartView, setChartView] = useState<ChartView>('impressions')

  useEffect(() => {
    if (!session?.organizationId) return
    let cancelled = false
    setIsLoading(true)
    setError(null)
    fetchOrgMetrics(session.organizationId, instance, parseInt(days))
      .then((d) => {
        if (!cancelled) setData(d)
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load metrics. Please try again.')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [session?.organizationId, instance, days])

  const hasScans = (data?.totalScans ?? 0) > 0

  const seriesData = (data?.series ?? []).map((p) => ({
    date: new Date(p.date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
    Impressions: Math.round(p.impressions),
    'QR Scans': p.scans,
  }))

  const periodImpressions = (data?.series ?? []).reduce(
    (sum, p) => sum + p.impressions,
    0,
  )
  const periodScans = (data?.series ?? []).reduce((sum, p) => sum + p.scans, 0)
  const periodLabel = `Last ${days} days`

  const isImpressionsView = chartView === 'impressions'
  const activeChartSeries = isImpressionsView
    ? [{ name: 'Impressions' as const, color: 'blue.5' }]
    : [{ name: 'QR Scans' as const, color: 'teal.5' }]
  const activeChartColor = isImpressionsView
    ? 'var(--mantine-color-blue-5)'
    : 'var(--mantine-color-teal-5)'

  return (
    <Stack gap='xl'>
      {/* Header */}
      <Group
        justify='space-between'
        align='center'
        h={{ base: 'auto', sm: 70 }}
        px={24}
        py={{ base: 16, sm: 0 }}
        style={{ boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.1)' }}
      >
        <Title fw={500} fz={{ base: 'h3', sm: 'h2' }}>
          Metrics
        </Title>
        <Select
          value={days}
          onChange={(v) => setDays(v ?? '30')}
          allowDeselect={false}
          data={[
            { value: '7', label: 'Last 7 days' },
            { value: '30', label: 'Last 30 days' },
            { value: '60', label: 'Last 60 days' },
            { value: '90', label: 'Last 90 days' },
          ]}
          w={160}
        />
      </Group>

      <Stack gap='xl' px={24} pb={24}>
        {error ? (
          <Alert color='red' title='Error'>{error}</Alert>
        ) : isLoading ? (
          <Stack gap='md'>
            <SimpleGrid cols={{ base: 2, sm: 4 }}>
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} h={90} radius='md' />
              ))}
            </SimpleGrid>
            <Skeleton h={300} radius='md' />
            <Skeleton h={200} radius='md' />
          </Stack>
        ) : (
          <Stack gap='lg'>
            {/* Stat cards */}
            <SimpleGrid cols={{ base: 2, sm: hasScans ? 4 : 2 }}>
              <StatCard
                label='Total impressions'
                value={data?.totalImpressions ?? 0}
                color='blue.7'
                sub='All time'
              />
              <StatCard
                label='Impressions'
                value={periodImpressions}
                color='blue.5'
                sub={periodLabel}
              />
              {hasScans && (
                <>
                  <StatCard
                    label='QR scans'
                    value={data?.totalScans ?? 0}
                    color='teal.7'
                    sub='All time'
                  />
                  <StatCard
                    label='QR scans'
                    value={periodScans}
                    color='teal.5'
                    sub={periodLabel}
                  />
                </>
              )}
            </SimpleGrid>

            {/* Time-series chart */}
            <Paper withBorder p='md' radius='md'>
              <Group justify='space-between' mb='md' align='center'>
                <Group gap={8} align='center'>
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: activeChartColor,
                      flexShrink: 0,
                    }}
                  />
                  <Title order={4}>
                    {isImpressionsView ? 'Impressions' : 'QR Scans'} over time
                  </Title>
                </Group>
                {hasScans && (
                  <SegmentedControl
                    size='xs'
                    value={chartView}
                    onChange={(v) => setChartView(v as ChartView)}
                    data={[
                      { label: 'Impressions', value: 'impressions' },
                      { label: 'QR Scans', value: 'scans' },
                    ]}
                  />
                )}
              </Group>
              {seriesData.length === 0 ? (
                <Text c='dimmed' py='xl' ta='center'>
                  No data for this period yet.
                </Text>
              ) : (
                <LineChart
                  h={260}
                  data={seriesData}
                  dataKey='date'
                  series={activeChartSeries}
                  curveType='monotone'
                  withDots={seriesData.length <= 60}
                  gridAxis='xy'
                  yAxisLabel={isImpressionsView ? 'Impressions' : 'QR Scans'}
                />
              )}
            </Paper>

            {/* By-ad breakdown */}
            {(data?.byAd ?? []).length > 0 && (
              <Paper withBorder p='md' radius='md'>
                <Title order={4} mb='sm'>
                  By advertisement
                </Title>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Ad</Table.Th>
                      <Table.Th>Location</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>
                        Impressions
                      </Table.Th>
                      {hasScans && (
                        <Table.Th style={{ textAlign: 'right' }}>
                          QR Scans
                        </Table.Th>
                      )}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {data!.byAd.map((row) => (
                      <Table.Tr key={row.deviceAdvertisementId}>
                        <Table.Td>
                          <Anchor
                            component={Link}
                            to={`/dashboard/myAds/${row.deviceAdvertisementId}`}
                            c={COLORS.HiddenGemzRed}
                            fw={500}
                          >
                            {row.adName || 'Untitled Ad'}
                          </Anchor>
                        </Table.Td>
                        <Table.Td>
                          <Text size='sm'>
                            {row.locationName ?? '—'}
                            {row.deviceName && (
                              <Text span size='xs' c='dimmed'>
                                {' · '}
                                {row.deviceName}
                              </Text>
                            )}
                          </Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          {Math.round(row.impressions).toLocaleString()}
                        </Table.Td>
                        {hasScans && (
                          <Table.Td style={{ textAlign: 'right' }}>
                            {row.scans.toLocaleString()}
                          </Table.Td>
                        )}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>
            )}

            {!isLoading &&
              (data?.totalImpressions ?? 0) === 0 &&
              (data?.totalScans ?? 0) === 0 && (
                <Text c='dimmed' ta='center' py='xl'>
                  No performance data yet. Check back once your ads start
                  running.
                </Text>
              )}
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}
